import { useEffect, useState, type ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { useDebounced } from '@/lib/catalogue';
import { PROVINCES, formatCodePostal, formatTelephone } from '@/lib/clientForm';
import {
  useCreerClient,
  useCustomers,
  useGererClient,
  useSupprimerClient,
  type Customer,
} from '@/lib/orders';

// Style commun des champs du formulaire de création.
const CHAMP = 'border-input bg-background h-11 w-full rounded-lg border px-3 text-base';

/**
 * Carnet d'adresses (admin) : rechercher/lister les clients et en créer un
 * nouveau, sans démarrer de commande. Les clients importés du CSV et ceux créés
 * ici cohabitent ; ces derniers portent un numéro WI- (à rapprocher de l'ERP).
 */
export default function Customers() {
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(30);
  const [page, setPage] = useState(0);
  const [onlyNew, setOnlyNew] = useState(false);
  const debounced = useDebounced(q, 250);
  // includeInactive : l'admin doit voir les clients désactivés pour les réactiver.
  const { data, isPending, isError } = useCustomers(
    debounced,
    true,
    limit,
    page * limit,
    onlyNew,
    true,
  );
  const creer = useCreerClient();
  const gerer = useGererClient();
  const supprimer = useSupprimerClient();
  const qc = useQueryClient();
  // Compte COMMUN (partagé, ex. « RABAIS ÉTUDIANT ») : exclu des cadeaux/concours.
  // Valable pour N'IMPORTE QUEL client (pas seulement walk-in).
  const commun = useMutation({
    mutationFn: ({ id, sharedAccount }: { id: number; sharedAccount: boolean }) =>
      api(`/api/customers/${id}/shared-account`, {
        method: 'PATCH',
        body: JSON.stringify({ sharedAccount }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['customers'] }),
  });
  // Client en cours d'édition (modale) et message d'erreur d'action (activer/supprimer).
  const [editClient, setEditClient] = useState<Customer | null>(null);
  const [actionMsg, setActionMsg] = useState('');

  async function basculerActif(c: Customer) {
    setActionMsg('');
    try {
      await gerer.mutateAsync({ id: c.id, isActive: !c.isActive });
    } catch (e) {
      setActionMsg(e instanceof Error ? e.message : 'Action impossible.');
    }
  }

  async function supprimerClient(c: Customer) {
    setActionMsg('');
    if (!window.confirm(`Supprimer définitivement « ${c.name} » (${c.custNum}) ?`)) return;
    try {
      await supprimer.mutateAsync(c.id);
    } catch (e) {
      setActionMsg(
        e instanceof ApiError
          ? e.message
          : 'Suppression impossible.',
      );
    }
  }
  // Retour à la première page quand la recherche, la taille de page ou le filtre change.
  useEffect(() => setPage(0), [debounced, limit, onlyNew]);
  // Compteurs globaux (indépendants de la recherche). Se rafraîchit après création
  // d'un client, car useCreerClient invalide toutes les requêtes « customers ».
  const stats = useQuery<{ total: number; walkIn: number }>({
    queryKey: ['customers', 'stats'],
    queryFn: () => api('/api/customers/stats'),
  });

  const vide = { name: '', phone: '', address: '', city: '', province: '', postalCode: '', email: '' };
  const [form, setForm] = useState(vide);
  // Section repliable : on la garde fermée par défaut pour dégager la liste.
  const [ouvert, setOuvert] = useState(false);

  const set = (champ: keyof typeof vide, valeur: string) =>
    setForm((f) => ({ ...f, [champ]: valeur }));
  // Champs texte libres saisis en MAJUSCULES (comme la fiche client des reps).
  const enMaj = (champ: keyof typeof vide) => (e: ChangeEvent<HTMLInputElement>) =>
    set(champ, e.target.value.toUpperCase());

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;
    await creer.mutateAsync({
      name: form.name.trim(),
      phone: form.phone.trim() || undefined,
      address: form.address.trim() || undefined,
      city: form.city.trim() || undefined,
      province: form.province.trim() || undefined,
      postalCode: form.postalCode.trim() || undefined,
      email: form.email.trim() || undefined,
    });
    setForm(vide);
  }

  const clients = data?.customers ?? [];

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="bg-card rounded-xl border p-4">
          <p className="text-muted-foreground text-sm">Clients au total</p>
          <p className="text-2xl font-bold tabular-nums">{stats.data?.total ?? '—'}</p>
        </div>
        <div className="bg-card rounded-xl border p-4">
          <p className="text-muted-foreground text-sm">Nouveaux clients (créés sur place)</p>
          <p className="text-2xl font-bold tabular-nums">{stats.data?.walkIn ?? '—'}</p>
        </div>
      </div>

      <section className="bg-card rounded-xl border">
        {/* En-tête cliquable : replie/déplie le formulaire de création. */}
        <button
          type="button"
          onClick={() => setOuvert((o) => !o)}
          aria-expanded={ouvert}
          className="flex w-full items-center justify-between gap-3 p-6 text-left"
        >
          <div>
            <h2 className="text-lg font-semibold">Nouveau client</h2>
            {!ouvert && (
              <p className="text-muted-foreground text-sm">
                Cliquez pour créer un client (numéro <span className="font-mono">WI-…</span>).
              </p>
            )}
          </div>
          <span aria-hidden className="text-muted-foreground text-xl">
            {ouvert ? '▾' : '▸'}
          </span>
        </button>

        {ouvert && (
          <div className="border-t p-6 pt-4">
            <p className="text-muted-foreground mb-4 text-sm">
              Un numéro <span className="font-mono">WI-…</span> lui sera attribué, à rapprocher de
              l'ERP après le show.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <label className="space-y-1.5">
                  <span className="block text-sm font-medium">Nom du client *</span>
                  <input value={form.name} onChange={enMaj('name')} required className={CHAMP} />
                </label>
                <label className="space-y-1.5">
                  <span className="block text-sm font-medium">Téléphone</span>
                  <input
                    value={form.phone}
                    onChange={(e) => set('phone', formatTelephone(e.target.value))}
                    inputMode="tel"
                    placeholder="(514) 555-1234"
                    pattern="\(\d{3}\) \d{3}-\d{4}"
                    title="Format : (514) 555-1234"
                    className={CHAMP}
                  />
                </label>
                <label className="space-y-1.5">
                  <span className="block text-sm font-medium">Adresse</span>
                  <input value={form.address} onChange={enMaj('address')} className={CHAMP} />
                </label>
                <label className="space-y-1.5">
                  <span className="block text-sm font-medium">Ville</span>
                  <input value={form.city} onChange={enMaj('city')} className={CHAMP} />
                </label>
                <label className="space-y-1.5">
                  <span className="block text-sm font-medium">Province</span>
                  <select
                    value={form.province}
                    onChange={(e) => set('province', e.target.value)}
                    className={CHAMP}
                  >
                    <option value="">—</option>
                    {PROVINCES.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="space-y-1.5">
                  <span className="block text-sm font-medium">Code postal</span>
                  <input
                    value={form.postalCode}
                    onChange={(e) => set('postalCode', formatCodePostal(e.target.value))}
                    placeholder="H0H 0H0"
                    pattern="[A-Z]\d[A-Z] \d[A-Z]\d"
                    title="Format : H0H 0H0"
                    className={CHAMP}
                  />
                </label>
                <label className="space-y-1.5">
                  <span className="block text-sm font-medium">Courriel</span>
                  <input
                    value={form.email}
                    onChange={enMaj('email')}
                    inputMode="email"
                    placeholder="NOM@DOMAINE.COM"
                    pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                    title="Adresse valide, ex. NOM@DOMAINE.COM"
                    className={CHAMP}
                  />
                </label>
              </div>

              {creer.isError && (
                <p className="text-destructive text-sm font-medium">
                  {(creer.error as Error).message}
                </p>
              )}

              <button
                type="submit"
                disabled={creer.isPending || !form.name.trim()}
                className="bg-primary text-primary-foreground h-11 rounded-lg px-5 font-medium disabled:opacity-40"
              >
                {creer.isPending ? 'Création…' : 'Créer le client'}
              </button>
            </form>
          </div>
        )}
      </section>

      <section className="bg-card space-y-4 rounded-xl border p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">Clients</h2>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setOnlyNew((v) => !v)}
              aria-pressed={onlyNew}
              className={`h-11 rounded-lg border px-4 text-sm font-medium ${
                onlyNew
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-input hover:border-primary/50'
              }`}
            >
              {onlyNew ? '✓ Nouveaux clients' : 'Nouveaux clients'}
            </button>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nom, n° de client ou ville…"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="border-input bg-background h-11 w-72 rounded-lg border px-3 text-base"
            />
            <label className="text-muted-foreground flex items-center gap-1.5 text-sm">
              Afficher
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="border-input bg-background h-11 rounded-lg border px-2 text-sm"
              >
                <option value={30}>30</option>
                <option value={100}>100</option>
                <option value={500}>500</option>
                <option value={2000}>Tout</option>
              </select>
            </label>
          </div>
        </div>

        {actionMsg && (
          <p className="text-destructive rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm font-medium">
            {actionMsg}
          </p>
        )}

        {isError ? (
          <p className="text-destructive text-sm">Clients indisponibles.</p>
        ) : isPending ? (
          <p className="text-muted-foreground text-sm">Chargement…</p>
        ) : clients.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {q
              ? `Aucun client pour « ${q} »${onlyNew ? ' parmi les nouveaux clients' : ''}.`
              : onlyNew
                ? 'Aucun nouveau client pour l’instant.'
                : 'Aucun client.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b text-left">
                  <th className="p-3 font-medium">N°</th>
                  <th className="p-3 font-medium">Nom</th>
                  <th className="p-3 font-medium">Ville</th>
                  <th className="p-3 font-medium">Courriel</th>
                  <th className="p-3 font-medium">Téléphone</th>
                  <th className="p-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr key={c.id} className={`border-t ${c.isActive ? '' : 'opacity-55'}`}>
                    <td className="p-3 font-mono text-xs whitespace-nowrap">
                      {c.custNum}
                      {c.isWalkIn && (
                        <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400">
                          walk-in
                        </span>
                      )}
                    </td>
                    <td className="p-3 font-medium">
                      {c.name}
                      {!c.isActive && (
                        <span className="text-muted-foreground border-input ml-2 rounded border px-1.5 py-0.5 text-[10px] font-medium">
                          désactivé
                        </span>
                      )}
                      {c.sharedAccount && (
                        <span className="ml-2 rounded bg-blue-500/15 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:text-blue-400">
                          compte commun
                        </span>
                      )}
                    </td>
                    <td className="text-muted-foreground p-3">
                      {[c.city, c.province].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td className="text-muted-foreground p-3">{c.email || '—'}</td>
                    <td className="text-muted-foreground p-3 whitespace-nowrap">{c.phone || '—'}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <div className="inline-flex gap-1.5">
                        {/* Bascule « compte commun » — disponible pour TOUT client. */}
                        <button
                          onClick={() => commun.mutate({ id: c.id, sharedAccount: !c.sharedAccount })}
                          disabled={commun.isPending}
                          title="Compte commun : exclu des cadeaux et du concours"
                          className={`rounded-md border px-2 py-1 text-xs font-medium disabled:opacity-40 ${
                            c.sharedAccount
                              ? 'border-blue-500/40 bg-blue-500/10 text-blue-700 dark:text-blue-400'
                              : 'border-input hover:border-primary/50'
                          }`}
                        >
                          {c.sharedAccount ? '✓ Commun' : 'Commun'}
                        </button>
                        {c.isWalkIn && (
                          <>
                            <button
                              onClick={() => setEditClient(c)}
                              className="border-input hover:border-primary/50 rounded-md border px-2 py-1 text-xs font-medium"
                            >
                              Modifier
                            </button>
                            <button
                              onClick={() => basculerActif(c)}
                              disabled={gerer.isPending}
                              className="border-input hover:border-primary/50 rounded-md border px-2 py-1 text-xs font-medium disabled:opacity-40"
                            >
                              {c.isActive ? 'Désactiver' : 'Activer'}
                            </button>
                            <button
                              onClick={() => supprimerClient(c)}
                              disabled={supprimer.isPending}
                              className="rounded-md border border-destructive/40 px-2 py-1 text-xs font-medium text-destructive hover:bg-destructive/5 disabled:opacity-40"
                            >
                              Supprimer
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-muted-foreground text-xs">
            {data && data.total > 0
              ? `${page * limit + 1}–${page * limit + clients.length} sur ${data.total} client(s)`
              : '0 client'}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="border-input rounded-lg border px-3 py-1.5 text-sm font-medium disabled:opacity-40"
            >
              ← Précédents
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!data || (page + 1) * limit >= data.total}
              className="border-input rounded-lg border px-3 py-1.5 text-sm font-medium disabled:opacity-40"
            >
              {limit} suivants →
            </button>
          </div>
        </div>
      </section>

      {editClient && (
        <ClientEditModal client={editClient} onClose={() => setEditClient(null)} />
      )}
    </div>
  );
}

/**
 * Modale de modification d'un nouveau client (walk-in). Mêmes champs formatés que
 * la création ; seul le nom est obligatoire. Enregistre via /manage.
 */
function ClientEditModal({ client, onClose }: { client: Customer; onClose: () => void }) {
  const gerer = useGererClient();
  const [form, setForm] = useState({
    name: (client.name ?? '').toUpperCase(),
    phone: formatTelephone(client.phone ?? ''),
    address: (client.address ?? '').toUpperCase(),
    city: (client.city ?? '').toUpperCase(),
    province: (client.province ?? '').toUpperCase(),
    postalCode: formatCodePostal(client.postalCode ?? ''),
    email: (client.email ?? '').toUpperCase(),
  });
  const set = (champ: keyof typeof form, valeur: string) =>
    setForm((f) => ({ ...f, [champ]: valeur }));
  const enMaj = (champ: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) =>
    set(champ, e.target.value.toUpperCase());

  async function enregistrer(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;
    await gerer.mutateAsync({
      id: client.id,
      name: form.name.trim(),
      phone: form.phone.trim(),
      address: form.address.trim(),
      city: form.city.trim(),
      province: form.province.trim(),
      postalCode: form.postalCode.trim(),
      email: form.email.trim(),
    });
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-card max-h-[90dvh] w-full max-w-2xl overflow-y-auto rounded-2xl border p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Modifier <span className="font-mono text-sm">{client.custNum}</span>
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-xl leading-none"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>

        <form onSubmit={enregistrer} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <label className="space-y-1.5">
              <span className="block text-sm font-medium">Nom du client *</span>
              <input value={form.name} onChange={enMaj('name')} required className={CHAMP} />
            </label>
            <label className="space-y-1.5">
              <span className="block text-sm font-medium">Téléphone</span>
              <input
                value={form.phone}
                onChange={(e) => set('phone', formatTelephone(e.target.value))}
                inputMode="tel"
                placeholder="(514) 555-1234"
                pattern="\(\d{3}\) \d{3}-\d{4}"
                title="Format : (514) 555-1234"
                className={CHAMP}
              />
            </label>
            <label className="space-y-1.5">
              <span className="block text-sm font-medium">Adresse</span>
              <input value={form.address} onChange={enMaj('address')} className={CHAMP} />
            </label>
            <label className="space-y-1.5">
              <span className="block text-sm font-medium">Ville</span>
              <input value={form.city} onChange={enMaj('city')} className={CHAMP} />
            </label>
            <label className="space-y-1.5">
              <span className="block text-sm font-medium">Province</span>
              <select
                value={form.province}
                onChange={(e) => set('province', e.target.value)}
                className={CHAMP}
              >
                <option value="">—</option>
                {PROVINCES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1.5">
              <span className="block text-sm font-medium">Code postal</span>
              <input
                value={form.postalCode}
                onChange={(e) => set('postalCode', formatCodePostal(e.target.value))}
                placeholder="H0H 0H0"
                pattern="[A-Z]\d[A-Z] \d[A-Z]\d"
                title="Format : H0H 0H0"
                className={CHAMP}
              />
            </label>
            <label className="space-y-1.5">
              <span className="block text-sm font-medium">Courriel</span>
              <input
                value={form.email}
                onChange={enMaj('email')}
                inputMode="email"
                placeholder="NOM@DOMAINE.COM"
                pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                title="Adresse valide, ex. NOM@DOMAINE.COM"
                className={CHAMP}
              />
            </label>
          </div>

          {gerer.isError && (
            <p className="text-destructive text-sm font-medium">{(gerer.error as Error).message}</p>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={gerer.isPending || !form.name.trim()}
              className="bg-primary text-primary-foreground h-11 rounded-lg px-5 font-medium disabled:opacity-40"
            >
              {gerer.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="border-input h-11 rounded-lg border px-5 font-medium"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
